// Shared form components for Indian fintech context: +91 mobile, dd/mm/yyyy DOB,
// state dropdown, 6-digit PIN, PAN validation, INR live formatting.
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Chandigarh", "Puducherry", "Jammu & Kashmir", "Ladakh",
  "Andaman & Nicobar", "Dadra & Nagar Haveli", "Daman & Diu", "Lakshadweep",
];

// Validators
export const isPAN = (v) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test((v || "").toUpperCase().trim());
export const isPIN = (v) => /^\d{6}$/.test(v || "");
export const isMobile = (v) => /^[6-9]\d{9}$/.test((v || "").replace(/\D/g, ""));
export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");
export const isVIN = (v) => /^[A-HJ-NPR-Z0-9]{11,17}$/.test((v || "").toUpperCase().trim());

// Indian-locale comma formatting (1,00,000 — lakhs/crores grouping)
export function formatINRLive(num) {
  if (num === "" || num == null) return "";
  const n = Number(String(num).replace(/[^\d.]/g, ""));
  if (isNaN(n)) return "";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export function parseINRInput(str) {
  if (!str) return 0;
  const cleaned = String(str).replace(/[^\d.]/g, "");
  return cleaned ? Number(cleaned) : 0;
}

// Generic text field with error
export function TextField({ id, label, value, onChange, error, hint, placeholder, type = "text", className = "", maxLength, autoComplete, testId, transform }) {
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        data-testid={testId || `input-${id}`}
        type={type}
        value={value ?? ""}
        maxLength={maxLength}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onChange={(e) => onChange(transform ? transform(e.target.value) : e.target.value)}
        className={cn(error && "border-red-400 focus-visible:ring-red-300")}
      />
      {error ? (
        <div className="text-xs text-red-600 mt-1" data-testid={`error-${id}`}>{error}</div>
      ) : hint ? (
        <div className="text-xs text-slate-500 mt-1">{hint}</div>
      ) : null}
    </div>
  );
}

// INR amount with live Indian-grouping commas
export function INRField({ id, label, value, onChange, error, hint, className = "", placeholder = "0", testId }) {
  const display = formatINRLive(value);
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">₹</span>
        <Input
          id={id}
          data-testid={testId || `inr-${id}`}
          type="text"
          inputMode="numeric"
          value={display}
          placeholder={placeholder}
          onChange={(e) => onChange(parseINRInput(e.target.value))}
          className={cn("pl-7 font-mono", error && "border-red-400 focus-visible:ring-red-300")}
        />
      </div>
      {error ? <div className="text-xs text-red-600 mt-1">{error}</div>
        : hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
    </div>
  );
}

// Mobile with fixed +91 prefix
export function MobileField({ id, label, value, onChange, error, className = "", testId }) {
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex">
        <div className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-600 text-sm font-mono">+91</div>
        <Input
          id={id}
          data-testid={testId || `mobile-${id}`}
          type="tel"
          inputMode="numeric"
          maxLength={10}
          value={value ?? ""}
          placeholder="98XXXXXXXX"
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
          className={cn("rounded-l-none font-mono", error && "border-red-400")}
        />
      </div>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

// Date of birth — shadcn calendar, displayed as dd/mm/yyyy
export function DOBField({ id = "dob", label = "Date of birth", value, onChange, fromYear = 1940, toYear, minAge = 0, error, className = "", testId }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const maxYear = toYear || today.getFullYear();
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const display = selected
    ? selected.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "";
  const toISO = (d) => {
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const defaultMonth = selected || new Date(maxYear - 25, 0);
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            data-testid={testId || id}
            className={cn("w-full justify-start text-left font-normal h-10", error && "border-red-400")}
          >
            <CalendarIcon size={16} className="mr-2 text-slate-500" />
            <span className={display ? "text-slate-900 font-mono" : "text-slate-400"}>{display || "dd/mm/yyyy"}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => { onChange(toISO(d)); setOpen(false); }}
            defaultMonth={defaultMonth}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={maxYear}
            disabled={(d) => d > today || (minAge > 0 && d > new Date(maxYear - minAge, today.getMonth(), today.getDate()))}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

export function StateField({ id = "state", label = "State", value, onChange, error, className = "", testId }) {
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={testId || "state-select"} className={cn(error && "border-red-400")}>
          <SelectValue placeholder="Select state" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

export function PinField({ id = "pincode", label = "PIN code", value, onChange, error, className = "", testId }) {
  return (
    <TextField
      id={id} testId={testId || "pin-input"} label={label} value={value} error={error}
      placeholder="6 digits"
      maxLength={6}
      transform={(v) => v.replace(/\D/g, "").slice(0, 6)}
      onChange={onChange}
    />
  );
}

export function PANField({ id = "pan", label = "PAN", value, onChange, error, className = "", testId }) {
  return (
    <TextField
      id={id} testId={testId || "pan-input"} label={label} value={value} error={error}
      placeholder="ABCDE1234F" maxLength={10}
      transform={(v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10)}
      onChange={onChange}
    />
  );
}

export function SelectField({ id, label, value, onChange, options, error, placeholder = "Select", className = "", testId }) {
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={testId || `select-${id}`} className={cn(error && "border-red-400")}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const label = typeof o === "string" ? o : o.label;
            return <SelectItem key={val} value={val}>{label}</SelectItem>;
          })}
        </SelectContent>
      </Select>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}
